/**
 * 主導覽的項目與「現在在哪一頁」的判斷。
 *
 * 抽成純函式是因為 `aria-current` 的判斷有兩條不明顯的規則：文章頁
 * （`/blog/<slug>`）也算在部落格上，而三個產品頁要讓「產品」觸發鈕帶上目前頁
 * 樣式。寫在元件裡就得靠人開一篇文章、開三個產品頁各看一眼，寫在這裡可以一行
 * 一個 case 驗。
 */

export type NavKey =
  | 'dictation'
  | 'transcription'
  | 'meetingNotes'
  | 'pricing'
  | 'blog'
  | 'download'

export interface NavItem {
  key: NavKey
  href: string
  /** i18n key（`site.nav.*`）。 */
  labelKey: string
  /** route 已上線時使用 next/link，未上線項目不假裝存在於 typed routes。 */
  shipped: boolean
}

/** 產品項多一行說明，桌面彈出層與手機面板共用同一份文字。 */
export interface ProductNavItem extends NavItem {
  /** i18n key（`site.nav.*Desc`）。 */
  descKey: string
}

/** 「產品」下拉選單裡的三項。 */
export const PRODUCT_ITEMS: ProductNavItem[] = [
  {
    key: 'dictation',
    href: '/dictation',
    labelKey: 'site.nav.dictation',
    descKey: 'site.nav.dictationDesc',
    shipped: true,
  },
  {
    key: 'transcription',
    href: '/transcription',
    labelKey: 'site.nav.transcription',
    descKey: 'site.nav.transcriptionDesc',
    shipped: true,
  },
  {
    key: 'meetingNotes',
    href: '/meeting-notes',
    labelKey: 'site.nav.meetingNotes',
    descKey: 'site.nav.meetingNotesDesc',
    shipped: true,
  },
]

/**
 * 導覽列上直接出現的項目（產品在 `PRODUCT_ITEMS`，收在下拉選單裡）。
 * 「下載 App」指向首頁的 `#download` 區塊，從任何一頁按都換到首頁那一區。
 */
export const NAV_ITEMS: NavItem[] = [
  { key: 'pricing', href: '/pricing', labelKey: 'site.nav.pricing', shipped: true },
  { key: 'blog', href: '/blog', labelKey: 'site.nav.blog', shipped: true },
  { key: 'download', href: '/#download', labelKey: 'site.nav.download', shipped: true },
]

/** 指向頁內區塊而不是一頁的項目，永遠不算「目前頁」，站在首頁也一樣。 */
const SECTION_LINK_KEYS: ReadonlySet<NavKey> = new Set(['download'])

// currentNavKey 要掃的是「所有導覽得到的頁」，不是只掃列上的項目——產品搬進
// 選單之後只掃 NAV_ITEMS 會讓三個產品頁都標不出目前頁。
const ALL_ITEMS: NavItem[] = [...PRODUCT_ITEMS, ...NAV_ITEMS]

/**
 * 目前所在的導覽項，沒有對應的就回 `null`（首頁、試轉、結果頁、法務頁都不在
 * 導覽裡，不該有任何一項被標成目前頁；「下載 App」是首頁的一個區塊，首頁也
 * 不把它標成目前頁）。
 */
export function currentNavKey(pathname: string | null | undefined): NavKey | null {
  if (!pathname) return null
  // 去掉結尾斜線，`/blog/` 與 `/blog` 是同一頁。
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  for (const item of ALL_ITEMS) {
    if (SECTION_LINK_KEYS.has(item.key)) continue
    if (path === item.href) return item.key
    // 文章頁也算在部落格上；其他項目沒有子頁，所以只有 blog 需要前綴比對。
    if (item.key === 'blog' && path.startsWith(`${item.href}/`)) return item.key
  }
  return null
}

/**
 * 是不是站在三個產品頁的其中一頁上。「產品」觸發鈕的目前頁底線用它決定——
 * 注意底線歸底線，`aria-current` 只掛在彈出層裡對應的那一個連結上，按鈕本身
 * 不掛：按鈕不是連結，它沒有「目前頁」這個語意。
 */
export function isProductPath(pathname: string | null | undefined): boolean {
  const key = currentNavKey(pathname)
  return PRODUCT_ITEMS.some((item) => item.key === key)
}
